import { isLocallyCompleted } from '../services/tutorialService';
// src/api/authService.js
import api from './api';
import {
  saveAuthToken,
  getAuthToken,
  removeAuthToken,
  saveUserProfile,
  getUserProfile
} from '../utils/localStorage';

// L'instance axios `api` a déjà baseURL='/api' → ici on ne met QUE le
// sous-chemin (sinon double préfixe /api/api/* → 404 → auto-guest écrasait
// la session au rechargement).
const API_URL = '/auth';

// Register a new user
// R35 : birthDate optionnelle (YYYY-MM-DD) — age-gating 18+ des duels à mise.
export const register = async (username, email, password, birthDate = null) => {
  try {
    const response = await api.post(`${API_URL}/register`, {
      // Un invite qui a termine le tutoriel ne doit pas le refaire
      tutorialCompleted: isLocallyCompleted(),
      username,
      email,
      password,
      ...(birthDate ? { birthDate } : {}),
    });

    if (response.data?.success && response.data?.token) {
      saveAuthToken(response.data.token);
      saveUserProfile(response.data.user);
    }

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de l\'inscription');
  }
};

// Login
export const login = async (identifier, password) => {
  try {
    const response = await api.post(`${API_URL}/login`, {
      username: identifier,
      password,
    });

    if (response.data?.success && response.data?.token) {
      saveAuthToken(response.data.token);
      saveUserProfile(response.data.user);
    }

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Identifiants incorrects');
  }
};

// Mot de passe oublié — génère un lien de réinitialisation (renvoyé en dev, faute de SMTP)
export const forgotPassword = async (usernameOrEmail) => {
  const response = await api.post(`${API_URL}/forgot-password`, { usernameOrEmail });
  return response.data; // { success, message, resetUrl? }
};

// Réinitialisation via token
export const resetPassword = async (token, newPassword) => {
  const response = await api.post(`${API_URL}/reset-password`, { token, newPassword });
  return response.data; // { success, message }
};

// Guest login
export const guestLogin = async () => {
  try {
    const response = await api.post(`${API_URL}/guest`);
    if (response.data?.success && response.data?.token) {
      saveAuthToken(response.data.token);
      saveUserProfile(response.data.user);
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de la connexion invité');
  }
};

// Login with Google — Google Identity Services (GIS)
// Flux : One Tap / bouton officiel → credential (ID token) → POST /auth/google
// → le serveur vérifie le jeton et renvoie notre JWT local.
let gisPromise = null;

function loadGisScript() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.google?.accounts?.id) return Promise.resolve(true);
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(!!window.google?.accounts?.id);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return gisPromise;
}

export const isGoogleConfigured = () => !!process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Connexion via popup One Tap (bouton personnalisé)
export const loginWithGoogle = async () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    throw new Error('Connexion Google non configurée : ajoutez REACT_APP_GOOGLE_CLIENT_ID dans client/.env');
  }
  const ok = await loadGisScript();
  if (!ok) throw new Error('Impossible de charger Google Identity Services');

  const credential = await new Promise((resolve, reject) => {
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => (resp.credential ? resolve(resp.credential) : reject(new Error('Connexion Google annulée'))),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.prompt(); // One Tap
      // Si l'utilisateur ferme le One Tap sans choisir → après 90s on abandonne
      setTimeout(() => reject(new Error('Connexion Google annulée')), 90000);
    } catch (e) {
      reject(e);
    }
  });

  return loginWithGoogleCredential(credential);
};

// Connexion avec un credential (ID token) déjà obtenu (bouton officiel GIS)
export const loginWithGoogleCredential = async (credential) => {
  try {
    const response = await api.post(`${API_URL}/google`, { credential });
    if (response.data?.success && response.data?.token) {
      saveAuthToken(response.data.token);
      saveUserProfile(response.data.user);
    }
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de la connexion Google');
  }
};

// Affiche le bouton Google officiel dans un élément du DOM
export const renderGoogleButton = async (element, onCredential) => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId || !element) return false;
  const ok = await loadGisScript();
  if (!ok) return false;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (resp) => { if (resp.credential && onCredential) onCredential(resp.credential); },
  });
  window.google.accounts.id.renderButton(element, {
    theme: 'outline',
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    locale: 'fr',
  });
  return true;
};

// Logout
export const logout = () => {
  removeAuthToken();
  localStorage.removeItem('elemental_duelists_user_profile');
  return { success: true };
};

// Check if user has a token
export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

// Verify token with server
export const verifyToken = async () => {
  try {
    const token = getAuthToken();
    if (!token) {
      return { success: false };
    }

    const response = await api.get(`${API_URL}/me`);
    if (response.data?.success) {
      saveUserProfile(response.data.user);
      return { success: true, data: { user: response.data.user } };
    }

    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

// Get current user from server
export const getCurrentUser = async () => {
  try {
    const response = await api.get(`${API_URL}/me`);
    if (response.data?.success) {
      return { user: response.data.user };
    }
    // Fallback to localStorage
    const profile = getUserProfile();
    return { user: profile || { id: 'guest', username: 'Joueur', isGuest: true } };
  } catch (error) {
    const profile = getUserProfile();
    return { user: profile || { id: 'guest', username: 'Joueur', isGuest: true } };
  }
};

export const loginUser = async (email, password) => login(email, password);
export const registerUser = async (username, email, password, birthDate) => register(username, email, password, birthDate);
export const logoutUser = () => logout();

// R35 : date de naissance — settable une seule fois (age-gating 18+)
export const setBirthDate = async (birthDate) => {
  try {
    const response = await api.post(`${API_URL}/age`, { birthDate });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur lors de l\'enregistrement de la date de naissance');
  }
};

// R51 : compagnon + avatar générés via prompt
// Étape 1 : génération serveur (JPEG bruts fond magenta) — peut prendre 30-60 s
export const companionGenerate = async (prompt) => {
  try {
    const response = await api.post(`${API_URL}/companion/generate`, { prompt }, { timeout: 180000 });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Générateur d’images indisponible');
  }
};

// Étape 2 : commit des PNG découpés (fond transparent) produits côté client
export const companionCommit = async (companionPng, avatarPng) => {
  try {
    const response = await api.post(`${API_URL}/companion/commit`, { companionPng, avatarPng }, { timeout: 60000 });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Erreur sauvegarde du compagnon');
  }
};

const authService = {
  register,
  login,
  guestLogin,
  loginWithGoogle,
  loginWithGoogleCredential,
  renderGoogleButton,
  isGoogleConfigured,
  logout,
  isAuthenticated,
  verifyToken,
  getCurrentUser,
  companionGenerate,
  companionCommit,
};

export default authService;
